import VisitServiceReviewDialog from './VisitServiceReviewDialog';
import React, { useEffect, useState } from 'react';
import API from './api';

function cls(...a){ return a.filter(Boolean).join(' '); }

function Pill({ children, tone='slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return <span className={cls('inline-flex items-center px-2 py-0.5 rounded-full border text-xs', map[tone])}>{children}</span>;
}

function Input({ label, value, onChange, type='text' }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
    </label>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <select
        value={value || ''}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      >
        {children}
      </select>
    </label>
  );
}

function Modal({ open, onClose, title, children, widthClass='max-w-xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className={cls('w-full bg-white rounded-2xl shadow-xl border border-slate-200', widthClass)} onMouseDown={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-slate-800">{title}</div>
          <button className="px-2 py-1 text-sm rounded-lg border border-slate-200 hover:bg-slate-50" onClick={onClose}>Close</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const FALLBACK_REASONS = [
  { code: 'parent_refused_after_arrival', label: 'Parent refused service after provider arrived', tone: 'green', risk_label: 'Lower risk', description: 'Provider arrived, but the family declined service before completion.' },
  { code: 'parent_cancelled_after_arrival', label: 'Parent cancelled after provider arrived', tone: 'green', risk_label: 'Lower risk', description: 'Provider arrived, but the family cancelled after arrival.' },
  { code: 'child_unavailable', label: 'Child unavailable', tone: 'green', risk_label: 'Lower risk', description: 'Provider arrived, but the child was not available for service.' },
  { code: 'no_show_after_arrival', label: 'No-show after arrival', tone: 'green', risk_label: 'Lower risk', description: 'Provider arrived for the scheduled visit, but the family was unavailable.' },
  { code: 'short_visit_parent_reason', label: 'Short visit due to parent/caregiver reason', tone: 'green', risk_label: 'Lower risk', description: 'The visit was shortened because of a family-side circumstance.' },
  { code: 'technical_issue', label: 'Technical issue', tone: 'yellow', risk_label: 'Review carefully', description: 'A device, GPS, network, or system issue affected EVV capture.' },
  { code: 'missing_parent_confirmation', label: 'Missing parent confirmation', tone: 'yellow', risk_label: 'Review carefully', description: 'The visit needs admin review because parent confirmation is missing.' },
  { code: 'manual_admin_review', label: 'Manual admin review', tone: 'orange', risk_label: 'High discretion', description: 'Admin reviewed the record and is manually resolving the EVV exception.' },
  { code: 'other_admin_review', label: 'Other admin review', tone: 'orange', risk_label: 'High discretion', description: 'Admin reviewed the record and selected an exception reason not covered above.' },
];

function fmtDate(dt) {
  if (!dt) return '';
  return String(dt).slice(0,10);
}

function fmtTime(dt) {
  if (!dt) return '—';
  const s = String(dt);
  if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  const m = s.match(/\s(\d{2}:\d{2})/);
  return m ? m[1] : s;
}

function fmtRange(v) {
  const start = v.scheduled_start_time || fmtTime(v.scheduled_at);
  const end = v.scheduled_end_time || '';
  return end ? `${start}-${end}` : start;
}

function studentLabel(v) {
  return `${v.student_last_name || ''}${v.student_last_name ? ', ' : ''}${v.student_first_name || ''}`.trim() || 'Student';
}

function issueLabel(issue) {
  const map = {
    missing_checkin_time: 'Missing check-in time',
    missing_checkout_time: 'Missing check-out time',
    missing_checkin_location: 'Missing check-in location',
    missing_checkout_location: 'Missing check-out location',
    missing_telehealth_link: 'Missing telehealth link',
    missing_evv_edit_reason: 'Missing admin reason',
    review_required: 'Admin review required',
  };
  return map[issue] || String(issue || '').replace(/_/g, ' ');
}

function statusTone(s) {
  if (s === 'complete') return 'emerald';
  if (s === 'pending') return 'amber';
  return 'red'; // exception
}

function riskClasses(tone) {
  if (tone === 'green') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (tone === 'yellow') return 'border-amber-200 bg-amber-50 text-amber-900';
  if (tone === 'orange') return 'border-orange-200 bg-orange-50 text-orange-900';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export default function AdminEVVExceptions() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [reviewVisitId, setReviewVisitId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [resolveModal, setResolveModal] = useState(false);
  const [resolveVisit, setResolveVisit] = useState(null);
  const [resolveReasonCode, setResolveReasonCode] = useState('');
  const [reasons, setReasons] = useState(FALLBACK_REASONS);

  async function load() {
    try {
      setErr('');
      setLoading(true);
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await API.get(`/evv/exceptions?${qs.toString()}`);
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      setErr('Failed to load EVV exceptions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    let mounted = true;
    API.get('/evv/resolve-reasons')
      .then((res) => {
        const next = Array.isArray(res.data?.reasons) ? res.data.reasons : [];
        if (mounted && next.length) setReasons(next);
      })
      .catch(() => {
        if (mounted) setReasons(FALLBACK_REASONS);
      });
    return () => { mounted = false; };
  }, []);

  function openResolve(v) {
    setResolveVisit(v);
    setResolveReasonCode('');
    setResolveModal(true);
  }

  async function doResolve() {
    try {
      if (!resolveVisit) return;
      if (!resolveReasonCode) return setErr('Reason required.');

      await API.post(`/evv/resolve/${resolveVisit.id}`, { reason_code: resolveReasonCode });
      setResolveModal(false);
      await load();
    } catch (e) {
      console.error(e);
      setErr('Failed to resolve EVV exception.');
    }
  }

  function buildMissing(v) {
    const missing = [];

    // basic time requirements
    if (!v.checkin_time) missing.push('check-in');
    if (!v.checkout_time) missing.push('check-out');

    // delivery-mode requirements
    const dm = v.delivery_mode || 'in_person';

    // telehealth/hybrid: telehealth_link required
    if ((dm === 'telehealth' || dm === 'hybrid') && (!v.telehealth_link || String(v.telehealth_link).trim() === '')) {
      missing.push('telehealth link');
    }

    // in_person/hybrid: GPS required (presence check)
    const needsGps = (dm === 'in_person' || dm === 'hybrid');
    if (needsGps) {
      if (v.checkin_lat == null || v.checkin_lon == null) missing.push('checkin loc');
      if (v.checkout_lat == null || v.checkout_lon == null) missing.push('checkout loc');
    }

    // manual edit reason missing ONLY when evv_edit_at exists but reason missing
    const hasEditAt = !!v.evv_edit_at;
    const hasReason = !!(v.evv_edit_reason && String(v.evv_edit_reason).trim());
    if (hasEditAt && !hasReason) missing.push('edit reason');
    if (!missing.length && v.evv_issue_hint) missing.push(issueLabel(v.evv_issue_hint));

    return missing;
  }

  const selectedReason = reasons.find((r) => r.code === resolveReasonCode);
  const reasonsByTone = {
    green: reasons.filter((r) => r.tone === 'green'),
    yellow: reasons.filter((r) => r.tone === 'yellow'),
    orange: reasons.filter((r) => r.tone === 'orange'),
  };

  return (
    <div className="p-3 md:p-6">
      <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-indigo-50 border border-slate-200 shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-lg md:text-xl font-semibold text-slate-900">EVV Exceptions</div>
            
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={load} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
              Refresh
            </button>
          </div>
        </div>

        {err ? <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div> : null}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
          <Input label="From" type="date" value={from} onChange={setFrom} />
          <Input label="To" type="date" value={to} onChange={setTo} />
          <div className="flex items-end">
            <button onClick={load} className="w-full px-3 py-2 rounded-xl bg-sky-600 text-white text-sm hover:bg-sky-700">
              Apply
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
	                <tr>
	                  <th className="text-left px-3 py-2">Date</th>
	                  <th className="text-left px-3 py-2">Student</th>
	                  <th className="text-left px-3 py-2">Teacher</th>
	                  <th className="text-left px-3 py-2">Actual</th>
	                  <th className="text-left px-3 py-2">EVV</th>
	                  <th className="text-left px-3 py-2">Missing</th>
	                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => {
	                  const studentName = studentLabel(v);
	                  const missing = buildMissing(v);

	                  return (
	                    <tr key={v.id} className="border-t border-slate-100">
	                      <td className="px-3 py-2 whitespace-nowrap">
	                        <div className="font-medium text-slate-900">{v.scheduled_date || fmtDate(v.scheduled_at)}</div>
	                        <div className="text-xs text-slate-600">{fmtRange(v)}</div>
	                      </td>

                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{studentName}</div>
                        <div className="text-xs text-slate-600">visit #{v.id}</div>
                      </td>

                      <td className="px-3 py-2">
	                        <div className="text-slate-900">{v.teacher_name}</div>
	                        <div className="text-xs text-slate-600">{v.delivery_mode} · {v.location_mode || 'location'}</div>
	                      </td>

	                      <td className="px-3 py-2 whitespace-nowrap">
	                        <div className="text-slate-900">{fmtTime(v.checkin_time)} / {fmtTime(v.checkout_time)}</div>
	                        <div className="text-xs text-slate-600">
	                          {v.actual_duration_minutes != null ? `${v.actual_duration_minutes} min` : 'duration —'}
	                        </div>
	                      </td>

	                      <td className="px-3 py-2 whitespace-nowrap">
                        <Pill tone={statusTone(v.evv_status)}>{v.evv_status}</Pill>
                        {v.evv_required ? <span className="ml-2"><Pill tone="sky">required</Pill></span> : null}
                        {(v.evv_checkin_source || v.evv_checkout_source) ? (
                          <div className="text-[11px] text-slate-500 mt-1">
                            src: {v.evv_checkin_source || '—'} / {v.evv_checkout_source || '—'}
                          </div>
                        ) : null}
                      </td>

	                      <td className="px-3 py-2">
	                        {missing.length ? (
	                          <div className="flex flex-wrap gap-1">
	                            {missing.map((m) => <Pill key={m} tone="amber">{m}</Pill>)}
	                          </div>
	                        ) : (
	                          <div className="text-xs text-slate-600">—</div>
                        )}
                        {v.evv_edit_reason ? (
                          <div className="text-xs text-slate-600 mt-1">
                            reason: {v.evv_edit_reason}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-3 py-2">
                        <button
                          className="px-2.5 py-1.5 rounded-lg text-xs bg-white border border-slate-200 hover:bg-slate-50"
                          onClick={() => openResolve(v)}
                        >
                          Resolve
                        </button><button className="mt-2 block rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs text-indigo-800" onClick={() => setReviewVisitId(v.visit_id || v.id)}>Review service & payroll</button>
                      </td>
                    </tr>
                  );
                })}

                {!rows.length ? (
                  <tr>
	                    <td className="px-3 py-6 text-slate-600" colSpan={7}>
	                      {loading ? 'Loading…' : 'No EVV exceptions found.'}
	                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal open={resolveModal} onClose={() => setResolveModal(false)} title="Resolve EVV exception" widthClass="max-w-3xl">
        {resolveVisit ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visit context</div>
                <div className="mt-2 text-sm text-slate-900 font-semibold">{studentLabel(resolveVisit)}</div>
                <div className="text-xs text-slate-600">Visit #{resolveVisit.id} · {resolveVisit.delivery_mode}</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Scheduled</div>
                    <div className="font-medium">{resolveVisit.scheduled_date || fmtDate(resolveVisit.scheduled_at)} · {fmtRange(resolveVisit)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Teacher</div>
                    <div className="font-medium">{resolveVisit.teacher_name || '—'}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Actual EVV capture</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Check-in</div>
                    <div className="font-medium">{fmtTime(resolveVisit.checkin_time)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Check-out</div>
                    <div className="font-medium">{fmtTime(resolveVisit.checkout_time)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Duration</div>
                    <div className="font-medium">{resolveVisit.actual_duration_minutes != null ? `${resolveVisit.actual_duration_minutes} min` : '—'}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Pill tone={statusTone(resolveVisit.evv_status)}>{resolveVisit.evv_status}</Pill>
                  <Pill tone="sky">src: {resolveVisit.evv_checkin_source || '—'} / {resolveVisit.evv_checkout_source || '—'}</Pill>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Detected issue</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {buildMissing(resolveVisit).map((m) => <Pill key={m} tone="amber">{m}</Pill>)}
                </div>
              </div>

              <Select label="Admin resolution reason (required)" value={resolveReasonCode} onChange={setResolveReasonCode}>
                <option value="">Select reason...</option>
                <optgroup label="Lower risk - service attempted / family-side issue">
                  {reasonsByTone.green.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Review carefully - verification / technical issue">
                  {reasonsByTone.yellow.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </optgroup>
                <optgroup label="High discretion - admin override">
                  {reasonsByTone.orange.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </optgroup>
              </Select>

              {selectedReason ? (
                <div className={cls('mt-3 rounded-2xl border p-3 text-sm', riskClasses(selectedReason.tone))}>
                  <div className="font-semibold">{selectedReason.risk_label}: {selectedReason.label}</div>
                  <div className="mt-1 opacity-90">{selectedReason.description}</div>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setResolveModal(false)} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={doResolve}
                disabled={!resolveReasonCode}
                className={cls(
                  'px-3 py-2 rounded-xl text-sm',
                  resolveReasonCode ? 'bg-sky-600 text-white hover:bg-sky-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                )}
              >
                Save
              </button>
            </div>
          </>
        ) : null}
      </Modal>
      {reviewVisitId && <VisitServiceReviewDialog visitId={reviewVisitId} onClose={() => setReviewVisitId(null)} onSaved={load} />}
    </div>
  );
}
